import { ElementRef, EventEmitter, HostBinding } from "@angular/core";
import { ControlValueAccessor, FormControl, FormGroup } from "@angular/forms";
import { isDefined } from "ionic-angular/util/util";
import * as tabbable from "tabbable";
import { BaseComponent } from "../../core/components/base.component";
import { classesObjectToString } from "../../core/helpers/classes-object-to-string";
import { Control } from "../business-objects/control/control.model";

/**
 * @todo this class should extend ControlComponent
 */
export abstract class ControlComponent extends BaseComponent implements ControlValueAccessor {
    definition: Control;
    formControl: FormControl;
    changes: EventEmitter<any> = new EventEmitter();
    _value: any;
    touched = new EventEmitter();
    /** @deprecated */
    formGroup?: FormGroup;

    @HostBinding("class") hostClass: string;

    constructor(
        protected elemRef: ElementRef
    ) {
        super();
    }

    ngOnInit(): void {
        this.updateHostClass();
    }

    propagateChange: any = () => {
        // noop
    };

    validateFn: any = () => {
        // noop
    };

    /**
     * Set the initial value
     * @param value
     */
    writeValue(value: any): void {
        if (isDefined(value)) {
            this.value = value;
        }
    }

    /**
     * Update the value. When the user enters a new value
     * @param value
     */
    updateValue(value: any): void {
        this.value = value;
        this.onTouched();
        this.changes.next(value);
        this.propagateChange(value);
    }

    validate(c: FormControl): () => any {
        return this.validateFn(c);
    }

    /**
     * Update the FormControl touched indicator
     */
    onTouched(): void {
        this.touched.next();
    }

    /**
     * Set the function to be called when the control receives a change event.
     */
    registerOnChange(fn): void {
        this.changes
            .takeUntil(this.ngUnsubscribe)
            .subscribe(fn);
    }

    /**
     * Set the function to be called when the control receives a touch event.
     */
    registerOnTouched(fn): void {
        this.touched
            .takeUntil(this.ngUnsubscribe)
            .subscribe(fn);
    }

    get value(): any {
        return this._value;
    }

    set value(val) {
        this._value = val;
    }

    /**
     * Returns a value from the fields config
     * @param key
     * @param defaultValue
     * @returns {any}
     */
    getConfigOption(key: string, defaultValue?: any): any {

        if (!isDefined(this.definition.config[key])) {
            return defaultValue;
        }

        return this.definition.config[key];
    }

    protected updateHostClass(): void {
        const classes = {
            input: true,
            "input-md": true,
            [this.getNameForClass()]: true,
            ...this.getClasses()
        };

        try {
            this.hostClass = classesObjectToString(classes);
        } catch (e) {
            console.warn("error while updating host class", e);
            this.hostClass = "input input-md";
        }
    }

    /**
     * Return a key/value dictionary, where the value is truthy the class will be added to the host element
     * @return {object}
     */
    protected getClasses(): object {
        return {};
    }

    protected getNameForClass(): string {
        try {
            return this.definition.name.replace(".", "-");
        } catch (e) {
            return "";
        }
    }

    getOption<X, T>(key: string, defaultValue: T): T {

        if (!isDefined(this.definition.config) || !isDefined(this.definition.config[key])) {
            return defaultValue;
        }

        return this.definition.config[key];
    }

    focus(): void {
        try {
            this.getTabbableElements()[0].focus();
        } catch (e) {
            console.warn("failed to focus element", e);
        }
    }

    getTabbableElements(): Array<HTMLElement> {
        return tabbable(this.getElementRef().nativeElement);
    }

    getElementRef(): ElementRef {
        return this.elemRef;
    }
}



// WEBPACK FOOTER //
// ./src/vas/controls/control.component.ts